import axios from 'axios';
import { User, UserRole, LoginCredentials } from '../types/auth';
import { getActiveTenantId } from './tenantContext';

/**
 * Authentication service
 * Uses Keycloak as the identity provider via Resource Owner Password Credentials (ROPC) flow.
 * User sees our own login page — Keycloak validates credentials behind the scenes.
 * Token returned is a properly signed RSA256 JWT from Keycloak.
 */

const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL || 'https://keycloak.bluefield-c55cee68.southindia.azurecontainerapps.io';
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM || 'finops';
const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'finops-web';
const COST_API_URL = import.meta.env.VITE_COST_API_URL
  ? `${import.meta.env.VITE_COST_API_URL}/api/v1`
  : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api/v1');

const PKCE_VERIFIER_KEY = 'sso_pkce_verifier';

function base64UrlEncode(bytes: Uint8Array): string {
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256(input: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(digest);
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

class AuthService {
  private accessToken: string | null = null;
  private storedRefreshToken: string | null = null;
  // Real, confirmed-live bug fix: Keycloak's refresh tokens are one-time-use
  // (revoked the instant they're redeemed) and access tokens expire every
  // 5 minutes - every page fires several parallel API calls, so whenever the
  // token expires, multiple 401s land at once and each independently called
  // refreshToken(), racing to redeem the SAME refresh token. Only the first
  // ever succeeded; every other concurrent call got "already used" from
  // Keycloak and force-redirected to /login, even though the session was
  // fine. This field makes every concurrent caller await the ONE real
  // in-flight refresh instead of starting their own.
  private refreshPromise: Promise<void> | null = null;

  /**
   * Login via Keycloak ROPC flow.
   * User sees our login page → we call Keycloak token endpoint → get signed JWT.
   *
   * RETAINED BUT NO LONGER SURFACED IN THE UI - do not delete.
   *
   * LoginPage now offers a single "Sign in with SSO" button (see the block
   * comment there): email+password still works, but it is collected on
   * Keycloak's own hosted page rather than ours. This method remains a
   * supported authentication path for programmatic/automated callers and as
   * a break-glass route if the redirect flow is ever unavailable.
   *
   * Note the inherent limitation that motivated the UI change: a direct grant
   * cannot complete an interactive required action, so any account carrying
   * one (CONFIGURE_TOTP, VERIFY_PROFILE, UPDATE_PASSWORD) fails here with a
   * generic invalid_grant no password reset can clear. The hosted page can
   * complete them. See the error mapping below, which exists precisely
   * because this path cannot distinguish those cases any other way.
   */
  async login(credentials: LoginCredentials): Promise<User> {
    try {
      const tokenUrl = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`;

      const params = new URLSearchParams();
      params.append('client_id', KEYCLOAK_CLIENT_ID);
      params.append('username', credentials.email);
      params.append('password', credentials.password);
      params.append('grant_type', 'password');
      params.append('scope', 'openid profile email');

      const response = await axios.post(tokenUrl, params, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        timeout: 10000,
      });

      this.accessToken = response.data.access_token;
      this.storedRefreshToken = response.data.refresh_token;

      localStorage.setItem('accessToken', this.accessToken!);
      localStorage.setItem('refreshToken', this.storedRefreshToken!);

      // Parse user info from the JWT payload (Keycloak includes email, name, roles)
      return this.parseUserFromToken(this.accessToken!);

    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errData = error.response?.data;
        if (errData?.error === 'invalid_grant') {
          // Keycloak returns invalid_grant for four distinct situations and
          // ONLY error_description separates them: a wrong password, a
          // pending required action, a disabled account, and a brute-force
          // lockout. Collapsing all four into "Invalid email or password"
          // sent an admin hunting a password problem for hours when the real
          // cause was a pending required action - which a password reset on
          // its own does not clear, so the message actively misdirected the
          // fix. ROPC (Direct Access Grant) cannot complete an interactive
          // action, so these are hard blocks the user cannot self-clear.
          const desc = String(errData?.error_description || '');

          if (/not fully set up/i.test(desc)) {
            throw new Error(
              'Your account has a pending setup action that blocks password sign-in. Ask an administrator to clear the required actions on your account, or use "Sign in with Microsoft" instead.'
            );
          }
          if (/disabled/i.test(desc)) {
            // Covers a disabled account and a brute-force lockout, which
            // Keycloak reports as "Account temporarily disabled".
            throw new Error(
              'This account is disabled, or temporarily locked after repeated failed sign-in attempts. Ask an administrator to re-enable it.'
            );
          }
          throw new Error('Invalid email or password');
        }
        if (errData?.error === 'unauthorized_client') {
          throw new Error('Authentication service unavailable');
        }
        // A network failure reaching Keycloak means we cannot authenticate
        // anyone — surface that honestly. There used to be a mockLogin()
        // fallback here holding a hardcoded password list; it was removed
        // because this branch is reachable in production (Keycloak down or
        // scaled to zero), so it let the live site hand out admin sessions
        // against a password that shipped in the public JS bundle.
        if (!error.response || error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
          throw new Error('Cannot reach the authentication service. Please check your connection or try again shortly.');
        }
        throw new Error(errData?.error_description || errData?.error || 'Login failed');
      }
      throw error;
    }
  }

  /**
   * Redirect the browser to Keycloak's hosted login page (Authorization Code + PKCE flow).
   * This is a real SSO redirect — Keycloak shows its own login page, including any
   * social/corporate identity-provider buttons configured on the realm, unlike the
   * password-grant `login()` above which bypasses Keycloak's UI entirely.
   */
  async redirectToSSOLogin(): Promise<void> {
    const verifier = randomVerifier();
    const challenge = base64UrlEncode(await sha256(verifier));
    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);

    const redirectUri = `${window.location.origin}/auth/callback`;
    const authUrl = new URL(`${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/auth`);
    authUrl.searchParams.set('client_id', KEYCLOAK_CLIENT_ID);
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', 'openid profile email');
    authUrl.searchParams.set('code_challenge', challenge);
    authUrl.searchParams.set('code_challenge_method', 'S256');

    window.location.href = authUrl.toString();
  }

  /**
   * Exchange the authorization code returned from Keycloak's redirect for tokens.
   * Called from the /auth/callback route after the browser comes back from Keycloak.
   */
  async handleSSOCallback(code: string): Promise<User> {
    const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    if (!verifier) {
      throw new Error('Missing PKCE verifier — please try signing in again.');
    }
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);

    const tokenUrl = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`;
    const params = new URLSearchParams();
    params.append('client_id', KEYCLOAK_CLIENT_ID);
    params.append('grant_type', 'authorization_code');
    params.append('code', code);
    params.append('redirect_uri', `${window.location.origin}/auth/callback`);
    params.append('code_verifier', verifier);

    const response = await axios.post(tokenUrl, params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      timeout: 10000,
    });

    this.accessToken = response.data.access_token;
    this.storedRefreshToken = response.data.refresh_token;
    localStorage.setItem('accessToken', this.accessToken!);
    localStorage.setItem('refreshToken', this.storedRefreshToken!);

    return this.parseUserFromToken(this.accessToken!);
  }

  /**
   * Parse user info from Keycloak JWT token payload.
   * Keycloak JWT contains: sub, email, name, given_name, family_name, realm_access.roles
   */
  private parseUserFromToken(token: string): User {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));

      // Extract roles from realm_access (Keycloak standard claim)
      const realmRoles: string[] = payload.realm_access?.roles || [];
      const roles = this.mapKeycloakRoles(realmRoles);

      return {
        id: payload.sub,
        email: payload.email || payload.preferred_username,
        name: payload.name || `${payload.given_name || ''} ${payload.family_name || ''}`.trim(),
        roles: roles.length > 0 ? roles : [UserRole.VIEWER],
        tenantId: payload.tenant_id || getActiveTenantId(),
        preferences: {
          darkMode: false,
          language: 'en',
          timezone: 'UTC',
        },
      };
    } catch {
      throw new Error('Failed to parse authentication token');
    }
  }

  /**
   * Map Keycloak realm roles to our platform roles
   */
  private mapKeycloakRoles(realmRoles: string[]): UserRole[] {
    const roles: UserRole[] = [];
    if (realmRoles.includes('finops-admin')) roles.push(UserRole.ADMIN);
    if (realmRoles.includes('finops-analyst')) roles.push(UserRole.ANALYST);
    if (realmRoles.includes('finops-viewer')) roles.push(UserRole.VIEWER);
    // Also map legacy role names in case someone logs in from old DB
    if (realmRoles.includes('admin')) roles.push(UserRole.ADMIN);
    if (realmRoles.includes('analyst')) roles.push(UserRole.ANALYST);
    return roles;
  }

  /**
   * Get current user from stored token (on page refresh)
   */
  async getCurrentUser(): Promise<User | null> {
    const token = this.accessToken || localStorage.getItem('accessToken');
    if (!token) return null;

    try {
      // Try to get user info from Keycloak userinfo endpoint (validates token is still valid)
      const userInfoUrl = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/userinfo`;
      const response = await axios.get(userInfoUrl, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });

      // Also get roles from token since userinfo may not include realm_access
      const user = this.parseUserFromToken(token);
      user.email = response.data.email || user.email;
      user.name = response.data.name || user.name;
      return user;

    } catch {
      // Token expired or invalid
      // Try refresh token
      const refreshToken = this.storedRefreshToken || localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          await this.refreshToken();
          const newToken = localStorage.getItem('accessToken');
          if (newToken) return this.parseUserFromToken(newToken);
        } catch {
          // Refresh failed, clear tokens
          this.clearTokens();
        }
      }
      return null;
    }
  }

  /**
   * Refresh the access token using the refresh token
   */
  async refreshToken(): Promise<void> {
    // Single-flight: if a refresh is already in progress, every other
    // caller awaits that SAME promise instead of redeeming the one-time-use
    // refresh token a second time (which Keycloak would reject outright).
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    this.refreshPromise = this.doRefreshToken().finally(() => {
      this.refreshPromise = null;
    });
    return this.refreshPromise;
  }

  private async doRefreshToken(): Promise<void> {
    const refreshToken = this.storedRefreshToken || localStorage.getItem('refreshToken');
    if (!refreshToken) throw new Error('No refresh token');

    const tokenUrl = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`;
    const params = new URLSearchParams();
    params.append('client_id', KEYCLOAK_CLIENT_ID);
    params.append('refresh_token', refreshToken);
    params.append('grant_type', 'refresh_token');

    const response = await axios.post(tokenUrl, params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      timeout: 10000,
    });

    this.accessToken = response.data.access_token;
    this.storedRefreshToken = response.data.refresh_token;
    localStorage.setItem('accessToken', this.accessToken!);
    localStorage.setItem('refreshToken', this.storedRefreshToken!);
  }

  /**
   * Logout — invalidate token in Keycloak
   */
  async logout(): Promise<void> {
    const refreshToken = this.storedRefreshToken || localStorage.getItem('refreshToken');
    try {
      if (refreshToken) {
        const logoutUrl = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/logout`;
        const params = new URLSearchParams();
        params.append('client_id', KEYCLOAK_CLIENT_ID);
        params.append('refresh_token', refreshToken);
        await axios.post(logoutUrl, params, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          timeout: 5000,
        });
      }
    } catch {
      // Continue logout even if server-side invalidation fails
    } finally {
      this.clearTokens();
    }
  }

  private clearTokens(): void {
    this.accessToken = null;
    this.storedRefreshToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  getAccessToken(): string | null {
    return this.accessToken || localStorage.getItem('accessToken');
  }

  /**
   * Self-service password change. Backend verifies currentPassword against
   * Keycloak before updating - this only ever changes the calling user's own
   * password (scoped server-side to the JWT's own subject claim).
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const token = this.getAccessToken();
    await axios.post(
      `${COST_API_URL}/auth/change-password`,
      { currentPassword, newPassword },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
    );
  }

  // mockLogin() removed: it held a hardcoded email+password list (all roles,
  // including admin) that Vite compiled into the public JS bundle, and it was
  // reachable in production via the network-error branch in login(). Local
  // development authenticates against a real Keycloak instance instead.
}

export const authService = new AuthService();
